import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { getMyDoars, getMyMarketplaceListings, getPublicRealities } from "../api/doarPlatformApi";
import { getPublicWorlds } from "../api/shareApi";
import RealityCard from "../components/reality/RealityCard";
import RealityHeader from "../components/reality/RealityHeader";
import RealityToDoARCollapse from "../components/reality/RealityToDoARCollapse";
import "./Reality.css";

const REALITY_INDEX_FILTERS = [
  { id: "all", label: "All Realities" },
  { id: "verified", label: "Verified" },
  { id: "grounded", label: "Grounded" },
  { id: "derived", label: "Derived" },
  { id: "generated", label: "Generated" },
  { id: "speculative", label: "Speculative" },
  { id: "unverified", label: "Unverified" },
];

const CAPABILITY_FILTERS = [
  { id: "all", label: "Everything" },
  { id: "ar", label: "AR", capability: "experience.viewAR" },
  { id: "play", label: "Playable", capability: "experience.play" },
  { id: "simulate", label: "Simulated", capability: "experience.simulate" },
  { id: "live", label: "Live", capability: "experience.live" },
  { id: "customize", label: "Customizable", capability: "transform.customize" },
  { id: "print", label: "Printable", capability: "make.print" },
];

function timeAgo(value) {
  if (!value) return "";
  const then = new Date(value).getTime();
  if (!Number.isFinite(then)) return "";
  const minutes = Math.floor(Math.max(0, Date.now() - then) / 60000);
  if (minutes < 2) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function displayDoarTitle(item, id) {
  const title = String(item?.title || "").trim();
  if (!title) return item?.kind === "product" ? "Untitled design object" : "Untitled DoAR";
  const normalizedId = String(id || "").trim().toLowerCase();
  const normalizedTitle = title.toLowerCase();
  const looksLikeOpaqueId = /^[a-z0-9_-]{10,}$/i.test(title) && !/\s/.test(title);
  const looksLikeNumericId = /^\d{12,}$/.test(title);
  if (normalizedTitle === normalizedId || looksLikeOpaqueId || looksLikeNumericId) {
    return item?.kind === "product" ? "Untitled design object" : "Untitled DoAR";
  }
  return title;
}

export default function TheDoARHome() {
  const { token } = useContext(AuthContext);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [indexFilter, setIndexFilter] = useState(REALITY_INDEX_FILTERS[0]);
  const [capabilityFilter, setCapabilityFilter] = useState(CAPABILITY_FILTERS[0]);
  const [realities, setRealities] = useState([]);
  const [marketRealities, setMarketRealities] = useState([]);
  const [worlds, setWorlds] = useState([]);
  const [doars, setDoars] = useState([]);
  const [listings, setListings] = useState([]);
  const [discovery, setDiscovery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.documentElement.dataset.doarSurface = "reality";
    return () => { delete document.documentElement.dataset.doarSurface; };
  }, []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError("");

    Promise.allSettled([
      getPublicRealities({
        search: query || undefined,
        realityClass: indexFilter.id === "all" ? undefined : indexFilter.id,
        capability: capabilityFilter.capability,
        limit: 24,
      }),
      getPublicRealities({ marketplace: true, limit: 4 }),
      getPublicWorlds(),
    ]).then(([realityResult, marketResult, worldResult]) => {
      if (!live) return;

      if (realityResult.status === "fulfilled") {
        setRealities(realityResult.value?.realities || []);
        setDiscovery(realityResult.value?.discovery || null);
      } else {
        setRealities([]);
        setError("Realities could not be loaded.");
      }

      if (marketResult.status === "fulfilled") setMarketRealities(marketResult.value?.realities || []);
      else setMarketRealities([]);

      if (worldResult.status === "fulfilled") setWorlds((worldResult.value?.items || []).slice(0, 4));
      else setWorlds([]);
    }).finally(() => {
      if (live) setLoading(false);
    });

    return () => { live = false; };
  }, [query, indexFilter, capabilityFilter]);

  useEffect(() => {
    if (!token) {
      setDoars([]);
      setListings([]);
      return undefined;
    }

    let live = true;
    Promise.allSettled([getMyDoars(8), getMyMarketplaceListings()])
      .then(([doarResult, listingResult]) => {
        if (!live) return;
        if (doarResult.status === "fulfilled") setDoars(doarResult.value?.doars || []);
        if (listingResult.status === "fulfilled") setListings(listingResult.value?.items || []);
      });
    return () => { live = false; };
  }, [token]);

  const listingByObject = useMemo(
    () => new Map(listings.map(item => [String(item.objectId), item])),
    [listings]
  );

  const indexEnabled = Boolean(discovery?.enabled && discovery?.groundingEnabled);
  const submitSearch = event => {
    event.preventDefault();
    setQuery(search.trim());
  };

  const clearSearch = () => {
    setSearch("");
    setQuery("");
  };

  const resultTitle = query
    ? `Realities for “${query}”`
    : indexFilter.id !== "all" && indexEnabled
      ? `${indexFilter.label} Realities`
      : "Explore Realities";

  return (
    <RealityToDoARCollapse>
      <main className="reality-home">
        <RealityHeader />

        <section className="reality-home__hero">
          <p>DoAR · Design Objects for Augmenting Reality</p>
          <h1>Experience reality.</h1>
          <p className="lead">
            Discover evidence-aware Realities you can view, play, augment, customize, make, share and use.
          </p>

          <form className="reality-search" onSubmit={submitSearch} role="search">
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search objects, spaces, products, ideas…"
              aria-label="Search Realities"
            />
            {query ? <button className="reality-search__clear" type="button" onClick={clearSearch}>Clear</button> : null}
            <button type="submit">Search</button>
          </form>
        </section>

        {indexEnabled ? (
          <section className="reality-discovery-controls" aria-label="Reality discovery filters">
            <div className="reality-index-filter" aria-label="Reality Index">
              <span className="reality-filter-label">Reality Index</span>
              <div className="reality-filter-scroll">
                {REALITY_INDEX_FILTERS.map(item => (
                  <button
                    key={item.id}
                    data-index-class={item.id}
                    className={indexFilter.id === item.id ? "active" : ""}
                    type="button"
                    onClick={() => setIndexFilter(item)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="reality-capability-filter" aria-label="Capability">
              <span className="reality-filter-label">Experience</span>
              <div className="reality-filter-scroll">
                {CAPABILITY_FILTERS.map(item => (
                  <button
                    key={item.id}
                    className={capabilityFilter.id === item.id ? "active" : ""}
                    type="button"
                    onClick={() => setCapabilityFilter(item)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </section>
        ) : (
          <div className="reality-filters" aria-label="Reality filters">
            {CAPABILITY_FILTERS.map(item => (
              <button
                key={item.id}
                className={capabilityFilter.id === item.id ? "active" : ""}
                type="button"
                onClick={() => setCapabilityFilter(item)}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}

        {token && doars.length ? (
          <section className="reality-section reality-section--compact">
            <div className="reality-section__head">
              <div>
                <p className="reality-kicker">Your DoARs</p>
                <h2>Continue your realities</h2>
              </div>
            </div>
            <div className="personal-shelf">
              {doars.slice(0, 8).map(item => {
                const id = String(item.doarId || item.id || item._id || "");
                const listing = listingByObject.get(id);
                return (
                  <a
                    className="personal-card"
                    key={id}
                    href={listing?.slug ? `/r/${encodeURIComponent(listing.slug)}` : `https://doarspace.com/d/${encodeURIComponent(id)}`}
                  >
                    <small>{item.kind === "product" ? "Design Object" : "DoAR"}</small>
                    <strong title={item.title || ""}>{displayDoarTitle(item, id)}</strong>
                    <span>
                      {listing ? "Public Reality" : "Private DoAR"}
                      {item.updatedAt ? ` · ${timeAgo(item.updatedAt)}` : ""}
                    </span>
                  </a>
                );
              })}
            </div>
          </section>
        ) : null}

        <section className="reality-section reality-section--explore" aria-live="polite">
          <div className="reality-section__head">
            <div>
              <p className="reality-kicker">Discover</p>
              <h2>{resultTitle}</h2>
            </div>
            {indexEnabled ? <span className="reality-section__hint">RI 0 is most grounded · RI 100 is most speculative</span> : null}
          </div>

          {loading ? (
            <div className="reality-grid">
              {[1,2,3,4,5,6,7,8].map(key => <i className="reality-skeleton" key={key}/>)}
            </div>
          ) : error ? (
            <div className="reality-empty">
              <h3>Reality is temporarily unavailable.</h3>
              <p>{error}</p>
              <button onClick={() => window.location.reload()} type="button">Retry</button>
            </div>
          ) : realities.length ? (
            <div className="reality-grid">
              {realities.map((reality, index) => (
                <RealityCard
                  key={reality.id}
                  reality={reality}
                  featured={!query && indexFilter.id === "all" && capabilityFilter.id === "all" && index === 0}
                />
              ))}
            </div>
          ) : (
            <div className="reality-empty">
              <h3>No Realities match this view yet.</h3>
              <p>Try a broader Reality Index or capability filter, or create a new Reality with ToDoAR.</p>
            </div>
          )}
        </section>

        {marketRealities.length ? (
          <section className="reality-section reality-section--compact">
            <div className="reality-section__head">
              <div>
                <p className="reality-kicker">Available</p>
                <h2>Realities you can obtain</h2>
              </div>
              <a href="/marketplace">Open Marketplace →</a>
            </div>
            <div className="reality-grid reality-grid--compact">
              {marketRealities.map(item => <RealityCard key={item.id} reality={item} compact />)}
            </div>
          </section>
        ) : null}

        {worlds.length ? (
          <section className="reality-section reality-section--compact">
            <div className="reality-section__head">
              <div>
                <p className="reality-kicker">Worlds</p>
                <h2>Realities in context</h2>
              </div>
            </div>
            <div className="reality-grid reality-grid--compact">
              {worlds.map(world => (
                <a className="reality-card reality-card--compact" key={world.shareToken} href={`/share/${encodeURIComponent(world.shareToken)}`}>
                  <div className={`reality-card__media${world.thumbnail ? " has-cover" : " no-cover"}`}>
                    {world.thumbnail ? <img src={world.thumbnail} alt="" loading="lazy"/> : null}
                    <div className="reality-card__fallback">
                      <span>World</span>
                      <small>Spatial context of Realities</small>
                    </div>
                  </div>
                  <div className="reality-card__body">
                    <div className="reality-card__eyebrow">World</div>
                    <h3>{world.title || "Untitled World"}</h3>
                    <div className="reality-card__meta"><span>Explore</span></div>
                  </div>
                </a>
              ))}
            </div>
          </section>
        ) : null}

        <div className="reality-strip reality-strip--single">
          <a href="/marketplace">
            <strong>Marketplace</strong>
            <span>Buy, license or commission available Realities.</span>
          </a>
        </div>

        <footer className="reality-footer">
          <span>© DoAR · Design Objects for Augmenting Reality</span>
          <div>
            <a href="/terms">Terms</a>
            <a href="/privacy">Privacy</a>
            <a href="/responsible-disclosure">Security</a>
          </div>
        </footer>
      </main>
    </RealityToDoARCollapse>
  );
}
